import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  const companyName = "OnRounds AI";
  const effectiveDate = "January 4, 2024";
  const contactInfo = "[ nnolasco@onrounds.com ]"; // Set your contact info

  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={10} lg={10} xl={10}>
          <Typography variant="h2" gutterBottom display="inline">
            Use and Disclosure of Protected Health Information (PHI)
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/documentation/policies-and-procedures">
              Policies And Procedures
            </Link>
            <Typography>Use and Disclosure of Protected Health Information (PHI)</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Box>
            <Box mb={2}>
              <Typography variant="body1" paragraph>
                <strong>Effective Date:</strong> {effectiveDate}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body1" paragraph>
                At {companyName}, we are committed to treating and using protected health information (PHI) about you
                responsibly. This Notice of Health Information Practices describes the personal information we collect,
                and how and when we use or disclose that information. It also describes your rights as they relate to
                your PHI. This notice applies to all records of your care generated by {companyName}.
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                Our Legal Duty
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body1" paragraph>
                We are required by law to maintain the privacy of your health information, provide you with this notice
                about our legal duties and privacy practices regarding health information about you, and follow the
                terms of the current notice.
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                How We May Use and Disclose Health Information About You
              </Typography>
            </Box>

            <Box mb={2}>
              <List>
                <ListItem>
                  <Typography variant="body1">
                    <b>For Treatment:</b> We may use health information about you to provide you with medical treatment
                    or services. We may disclose health information about you to doctors, nurses, technicians, medical
                    students, or other healthcare personnel who are involved in taking care of you.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <b>For Payment:</b> We may use and disclose health information about your treatment and services to
                    bill and collect payment from you, your insurance company, or a third party.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <b>For Health Care Operations:</b> We may use and disclose health information about you for
                    operational purposes. These uses and disclosures are necessary to run {companyName} and ensure that
                    all of our patients receive quality care.
                  </Typography>
                </ListItem>
              </List>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                Additional Uses of Information
              </Typography>
            </Box>

            <Box mb={2}>
              <List>
                <ListItem>
                  <Typography variant="body1">
                    <b>Appointment Reminders:</b> We may use and disclose health information to contact you as a
                    reminder that you have an appointment for treatment or medical care.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <b>Health-Related Benefits and Services:</b> We may use and disclose health information to tell you
                    about health-related benefits, services, or medical education classes that may be of interest to
                    you.
                  </Typography>
                </ListItem>
              </List>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                Your Rights
              </Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body1">
                You have the following rights regarding health information we maintain about you:
              </Typography>
              <List>
                <ListItem>
                  <Typography variant="body1">
                    <b>Right to Inspect and Copy:</b> You have the right to inspect and copy health information that may
                    be used to make decisions about your care or payment for your care.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <b>Right to Amend:</b> If you feel that health information we have about you is incorrect or
                    incomplete, you may ask us to amend the information.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <b>Right to an Accounting of Disclosures:</b> You have the right to request an accounting of certain
                    disclosures we made of health information for purposes other than treatment, payment, or healthcare
                    operations.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <b>Right to Request Restrictions:</b> You have the right to request a restriction or limitation on
                    the health information we use or disclose about you for treatment, payment, or healthcare
                    operations.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <b>Right to Request Confidential Communications:</b> You have the right to request that we
                    communicate with you about medical matters in a certain way or at a certain location.
                  </Typography>
                </ListItem>
              </List>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                Changes to This Notice
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to change this notice and make the new notice apply to health information we
                already have as well as any information we receive in the future. We will post a current copy of the
                notice in {companyName}.
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                Complaints
              </Typography>
              <Typography variant="body1" paragraph>
                If you believe your privacy rights have been violated, you may file a complaint with us or with the
                Secretary of the Department of Health and Human Services.
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Typography variant="body1" paragraph>
                For more information about our privacy practices, or for additional copies of this notice, please
                contact:
              </Typography>
              <Typography variant="body1" paragraph>
                {contactInfo}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
